import Dialog from "Components/DesignSystem/Dialog/Dialog"
import Button from "Components/DesignSystem/Button/Button"
import Alert from "Components/DesignSystem/Alert/Alert"
import Typography from "Components/DesignSystem/Typography/Typography"
import Checkbox from "Components/DesignSystem/Checkbox/Checkbox"
import React, { useEffect, useState } from "react"
import { DELETE_ALL_ENVIRONMENTS_LABEL, DELETE_ENVIRONMENT_WARNING } from "../ProductConstants"


type MyProps = {
  isOpen: boolean,
  /** Receives the "delete from all other environments" choice — one decision for every row in the file. */
  handleConfirm: (allEnvironments: boolean) => void,
  onClose: Function
  loading: boolean
  /** `gmsproducts_fulldelete`. Without it the checkbox is not rendered and Delete sends `false`. */
  canDeleteAllEnvironments?: boolean
}

/**
 * The "are you sure" step of bulk-delete-via-XLS. On DS `Dialog` since § 126.
 *
 * The confirm stays a `primary` button — the § 53 destructive-confirm grammar, which
 * deliberately does NOT use `Button variant="danger"` (that variant is an outlined
 * destructive ACTION, not a confirm; `Dialog.tsx` spells this out). Legacy painted it
 * as a filled red `FillButton buttonStyle="danger"`. The hand-rolled
 * `tw-border-alert tw-bg-alert` box with its imported `alert-icons.svg` is a DS
 * `Alert` now.
 *
 * **The tone speaks once, and it speaks from the `Alert` (§ 166).** This dialog also
 * passed `tone="danger"`, so the header showed a red `CrossCircle` and the body a
 * yellow `AlertTriangle` — two marks at two different severities for one action, which
 * leaves a reader no way to tell which one to believe. The alert keeps the mark here
 * (unlike `DeleteProjectModal`, which drops it) because the body has *other* content
 * for the tint to stand out from: a heading above it and the question below it.
 *
 * **No `closeOnOutsideClick` here, deliberately.** The other PDW dialogs opt in
 * because `ModalContainer` gave them scrim-dismiss; this one is a confirmation for a
 * permanent delete, and a stray click beside the panel should not be one of the ways
 * to answer it. Escape and Cancel both still work.
 *
 * Both buttons gained `data-testid` — they carried `id` only, flagged in
 * `okf/pdw/e2e.md` § 4.
 *
 * The "Also delete this product from all other environments" checkbox applies to every
 * row of the uploaded file as one batch decision (not per row), and resets to unchecked
 * each time the dialog opens. Same copy as the product list's Bulk Delete dialog
 * (`ProductConstants`). No selected-count line here: the file, not a selection, decides
 * how many products are affected. The checkbox is rendered only for
 * `gmsproducts_fulldelete` holders (`canDeleteAllEnvironments`, hidden by default).
 */
const ConfirmBulkDelete = ({
  isOpen,
  onClose,
  handleConfirm,
  loading,
  canDeleteAllEnvironments = false
}: MyProps) => {
    const [allEnvironments, setAllEnvironments] = useState<boolean>(false);

    useEffect(() => {
      if (isOpen) setAllEnvironments(false);
    }, [isOpen]);

    const footer = (
      <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
        <Button
          variant="secondary"
          id="pdw-confirm-bulk-delete-modal-cancel-btn"
          data-testid="pdw-confirm-bulk-delete-modal-cancel-btn"
          onClick={() => onClose()}
        >
          Cancel
        </Button>
        <Button
          variant="danger-primary"
          id="pdw-confirm-bulk-delete-modal-delete-btn"
          data-testid="pdw-confirm-bulk-delete-modal-delete-btn"
          disabled={loading}
          onClick={() => handleConfirm(canDeleteAllEnvironments && allEnvironments)}
        >
          Delete
        </Button>
      </div>
    );

    return (
      <Dialog
        open={isOpen}
        onClose={() => onClose()}
        title="Upload XLS - Bulk Delete"
        footer={footer}
        data-testid="pdw-confirm-bulk-delete-modal"
      >
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Alert tone="danger" data-testid="pdw-confirm-bulk-delete-warning">
            {DELETE_ENVIRONMENT_WARNING}
          </Alert>
          {canDeleteAllEnvironments && (
            <Checkbox
              label={DELETE_ALL_ENVIRONMENTS_LABEL}
              checked={allEnvironments}
              onChange={(checked) => setAllEnvironments(checked)}
              disabled={loading}
              data-testid="pdw-confirm-bulk-delete-all-environments"
            />
          )}
          <Typography variant="body-primary" as="p">
            Would you like to proceed?
          </Typography>
        </div>
      </Dialog>
    )
}

export default ConfirmBulkDelete
